import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/services/api";
import { handleApiError, handleApiSuccess } from "@/utils/errorHandler";
import type {
  LiveStreamState,
  UpdateLiveStreamRequest,
} from "@/types";

// Device-reported status (displayStatus/deviceStatus) can change without any
// user action, so active streams are polled.
const LIVE_STREAM_POLL_INTERVAL_MS = 10_000;
// A session without an active stream is polled more slowly so a later stream
// started from Trackcast still appears without a page refresh.
const LIVE_STREAM_INACTIVE_POLL_INTERVAL_MS = 30_000;

export function getLiveStreamPollInterval(
  state?: LiveStreamState,
): number {
  if (!state || state === "ended" || state === "deleted") {
    return LIVE_STREAM_INACTIVE_POLL_INTERVAL_MS;
  }
  return LIVE_STREAM_POLL_INTERVAL_MS;
}

export function useLiveStream(sessionId: string) {
  return useQuery({
    queryKey: ["sessions", sessionId, "live-stream"],
    queryFn: () => apiClient.getLiveStream(sessionId),
    enabled: !!sessionId,
    refetchInterval: (query) =>
      getLiveStreamPollInterval(query.state.data?.state),
    retry: (failureCount, error: unknown) => {
      // Do not retry on 404 (session may not have a live stream yet)
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useLiveStreamLatency(sessionId: string) {
  return useQuery({
    queryKey: ["sessions", sessionId, "live-stream", "latency"],
    queryFn: () => apiClient.getLiveStreamLatency(sessionId),
    enabled: !!sessionId,
    // ~2s: latency-rx samples per IDR (GOP 60 @ 30fps), so latest_ms only updates this often —
    // polling faster just re-fetches the same value.
    refetchInterval: 2000,
    retry: (failureCount, error: unknown) => {
      // Don't retry on 403 (no view access — e.g. a viewer who shares no
      // active team with the owner viewing a non-public session). Teammates
      // share an active team with the owner, so 403 won't fire for them even
      // on private sessions. "No data yet" is not an error: the backend
      // answers 202 with data: null, which resolves here as null.
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 403) return false;
      return failureCount < 2;
    },
  });
}

export function useLiveStreamHistory(
  sessionId: string,
  enabled = true,
  latestStreamId?: string,
) {
  return useQuery({
    queryKey: [
      "sessions",
      sessionId,
      "live-stream",
      "history",
      latestStreamId ?? null,
    ],
    queryFn: () => apiClient.getLiveStreamHistory(sessionId),
    enabled: !!sessionId && enabled,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === sessionId ? previousData : undefined,
  });
}

export function useUpdateLiveStream(sessionId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      streamId,
      ...data
    }: { streamId: string } & UpdateLiveStreamRequest) =>
      apiClient.updateLiveStream(sessionId, streamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sessions", sessionId, "live-stream"],
      });
      handleApiSuccess(t("pages:sessions.detail.liveStream.toast.updateSuccess"));
    },
    onError: (error) => {
      handleApiError(error, t("pages:sessions.detail.liveStream.toast.updateFailed"));
    },
  });
}

export function useDeleteLiveStream(sessionId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (streamId: string) =>
      apiClient.deleteLiveStream(sessionId, streamId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sessions", sessionId, "live-stream"],
      });
      handleApiSuccess(t("pages:sessions.detail.liveStream.toast.deleteSuccess"));
    },
    onError: (error) => {
      handleApiError(error, t("pages:sessions.detail.liveStream.toast.deleteFailed"));
    },
  });
}
